import type {
  CorreccionVoz,
  EstiloExtraido,
  Modalidad,
  Patron,
  PerfilEstilo,
  Plan,
  PreferenciasEstilo,
  Reporte,
  EstadoReporte,
  RichReportBlock,
  RichReportDocument,
  RichReportInline,
  RichReportMark,
  Rol,
  Usuario,
} from '@/types'

type DbRow = Record<string, unknown>

export function mapDbUserToUsuario(row: DbRow | null | undefined): Usuario | null {
  if (!row) return null

  return {
    id: asString(row.id),
    email: asString(row.email),
    nombre: asString(row.name),
    foto_url: optionalString(row.photo_url),
    plan: asPlan(row.plan),
    rol: asRol(row.role),
    especialidades: asStringArray(row.specialty),
    modalidades: asModalidadArray(row.modalities),
    modalidad_preferida: optionalModalidad(row.preferred_modality),
    tema: row.theme === 'claro' ? 'claro' : 'oscuro',
    tamano_fuente: row.font_size === 'sm' || row.font_size === 'lg' ? row.font_size : 'md',
    onboarding_completado: Boolean(row.onboarding_completed),
    reportes_hoy: asNumber(row.reports_today),
    consultas_hoy: asNumber(row.ai_queries_today),
    ultima_fecha_reset: asString(row.last_reset_date),
  }
}

export function mapDbPatternToPatron(row: DbRow): Patron {
  return {
    id: asString(row.id),
    autor_id: asString(row.author_id),
    organizacion_id: optionalString(row.organization_id),
    titulo: asString(row.title),
    contenido: asString(row.content),
    modalidad: asModalidad(row.modality),
    seccion: asString(row.section, 'General'),
    comando_voz: asString(row.voice_command),
    comando_voz_personalizado: optionalString(row.custom_voice_command),
    es_publico: Boolean(row.is_public),
    esta_activo: row.is_active !== false,
    veces_usado: asNumber(row.use_count),
    lote_importacion_id: optionalString(row.import_batch_id),
    creado_en: asString(row.created_at),
  }
}

export function mapDbReportToReporte(row: DbRow): Reporte {
  return {
    id: asString(row.id),
    autor_id: asString(row.author_id),
    organizacion_id: optionalString(row.organization_id),
    contenido: asString(row.content),
    contenido_rico: asRichReportDocument(row.content_json),
    dictado_raw: asString(row.dictation_raw),
    patron_id: optionalString(row.pattern_id),
    modalidad: asModalidad(row.modality),
    duracion_segundos: asNumber(row.duration_seconds),
    tipo_estudio: optionalString(row.study_type),
    uso_ia: row.ai_used === true,
    utilidad_ia: row.ai_helpfulness === 'util' || row.ai_helpfulness === 'no_util' || row.ai_helpfulness === 'no_seguro' ? row.ai_helpfulness : null,
    version_medicion: typeof row.measurement_version === 'number' ? row.measurement_version : null,
    actividad_segundos: asNumber(row.active_seconds),
    version_actividad: typeof row.activity_measurement_version === 'number' ? row.activity_measurement_version : null,
    perfil_estilo_id: optionalString(row.style_profile_id),
    estado: asEstadoReporte(row.status),
    creado_en: asString(row.created_at),
    actualizado_en: asString(row.updated_at),
  }
}

export function mapDbStyleProfileToPerfilEstilo(row: DbRow | null | undefined): PerfilEstilo | null {
  if (!row) return null

  return {
    id: asString(row.id),
    usuario_id: asString(row.user_id),
    reportes_ejemplo: asStringArray(row.example_reports),
    estilo_extraido: asRecord(row.extracted_style) as unknown as EstiloExtraido,
    preferencias: asRecord(row.preferences) as unknown as PreferenciasEstilo,
    actualizado_en: asString(row.updated_at),
  }
}

export function mapDbVoiceCorrectionToCorreccionVoz(row: DbRow): CorreccionVoz {
  return {
    id: asString(row.id),
    usuario_id: asString(row.user_id),
    termino_incorrecto: asString(row.wrong_term),
    termino_correcto: asString(row.correct_term),
  }
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

function asNumber(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0
}

function asModalidad(value: unknown): Modalidad {
  return value === 'TAC' || value === 'ECO' || value === 'RM' || value === 'MAMO' ? value : 'RX'
}

function asEstadoReporte(value: unknown): EstadoReporte {
  return value === 'dictado' ? 'dictado' : 'en_progreso'
}

function optionalModalidad(value: unknown): Modalidad | undefined {
  return value === 'RX' || value === 'TAC' || value === 'ECO' || value === 'RM' || value === 'MAMO'
    ? value
    : undefined
}

function asModalidadArray(value: unknown): Modalidad[] {
  return Array.isArray(value) ? value.map(asModalidad) : []
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {}
}

function asRichReportDocument(value: unknown): RichReportDocument | null {
  if (!isRecord(value) || value.version !== 1 || !Array.isArray(value.blocks)) return null

  const blocks: RichReportBlock[] = []
  for (const block of value.blocks) {
    const parsed = asRichReportBlock(block)
    if (!parsed) return null
    blocks.push(parsed)
  }

  return { version: 1, blocks }
}

function asRichReportBlock(value: unknown): RichReportBlock | null {
  if (!isRecord(value)) return null
  const type = value.type === 'paragraph' || value.type === 'bullet' ? value.type : null
  if (typeof value.id !== 'string' || !type || !Array.isArray(value.children)) return null

  const children: RichReportInline[] = []
  for (const child of value.children) {
    const parsed = asRichReportInline(child)
    if (!parsed) return null
    children.push(parsed)
  }

  return {
    id: value.id,
    type,
    children,
  }
}

function asRichReportInline(value: unknown): RichReportInline | null {
  if (!isRecord(value) || typeof value.text !== 'string') return null
  const rawMarks = Array.isArray(value.marks) ? value.marks : []
  const marks: RichReportMark[] = []

  for (const mark of rawMarks) {
    if (mark !== 'bold' && mark !== 'italic') return null
    if (!marks.includes(mark)) marks.push(mark)
  }

  return {
    text: value.text,
    marks,
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function asPlan(value: unknown): Plan {
  return value === 'pro' || value === 'pro_plus' ? value : 'free'
}

function asRol(value: unknown): Rol {
  return value === 'admin' ? 'admin' : 'radiologo'
}
